import { type Result, type ResultAsync, err, ok } from "./result";

type Operation<T> = Promise<T> | (() => T) | (() => Promise<T>);

/**
 * This function packages the result of a synchronous or asynchronous fallible
 * operation, whether that's a success or a failure. It provides a type-safe
 * container for dealing with errors without having to use a try/catch block.
 */
function attempt<T, E = Error>(promise: Promise<T>): ResultAsync<T, E>;
function attempt<T, E = Error>(operation: () => Promise<T>): ResultAsync<T, E>;
function attempt<T, E = Error>(operation: () => T): Result<T, E>;
function attempt<T, E = Error>(
  operation: Operation<T>,
): Result<T, E> | ResultAsync<T, E> {
  if (operation instanceof Promise) {
    return operation.then(ok).catch(err);
  }

  try {
    const result = operation();

    if (result instanceof Promise) {
      return result.then(ok).catch(err);
    }

    return ok(result);
  } catch (error) {
    return err(error as E);
  }
}

export { attempt };
